import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2 } from 'lucide-react';
import { STICKERS, getSticker } from '../../lib/stickers';

const PRESET_PALETTES = [
  { bg: '#fce7f3', border: '#f9a8c8', ink: '#9d174d' },  // pink
  { bg: '#ede9fe', border: '#c4b5fd', ink: '#5b21b6' },  // lilac
  { bg: '#dcfce7', border: '#86efac', ink: '#166534' },  // mint
  { bg: '#fef3c7', border: '#fde68a', ink: '#92400e' },  // amber
  { bg: '#dcecfa', border: '#a9c8eb', ink: '#1e3a5b' },  // sky
  { bg: '#ffe4d1', border: '#f5c199', ink: '#7c2d12' },  // peach
];

/**
 * Compact stickers rail — grid cell (pitching grid) + next to brand name (talent view).
 * `hasPP` and `hasEngaged` are auto-derived flags. `manualStickers` are ids toggled on
 * for this (brand, talent). `customStickers` come from the DB (managed by the user).
 */
export const StickersRail = ({ hasPP, hasEngaged, manualStickers = [], canEdit, onToggle, customStickers = [], onCreateSticker, onDeleteSticker }) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ emoji: '', label: '', palette: 0 });
  const btnRef = useRef(null);

  const openPicker = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setAnchor({ top: r.bottom + 4, left: r.left });
    }
    setOpen((o) => !o);
    setCreating(false);
  };

  const stickerById = (id) => getSticker(id) || customStickers.find((s) => s.id === id) || null;

  // Union of auto flags + manual selections, keeping insertion order
  const shown = [];
  const push = (id) => { if (!shown.includes(id)) shown.push(id); };
  if (hasPP) push('pp');
  if (hasEngaged) push('engaged');
  manualStickers.forEach((s) => push(s));

  const renderPill = (id) => {
    const meta = stickerById(id);
    if (!meta) {
      return (
        <span key={id} className="inline-flex items-center h-5 px-1 rounded-full text-[11px] leading-none bg-white/70 border border-slate-200">{id}</span>
      );
    }
    return (
      <span
        key={meta.id}
        className="inline-flex items-center gap-0.5 px-1 h-5 rounded-full text-[10px] font-semibold whitespace-nowrap"
        style={{ background: meta.bg, borderWidth: 1, borderStyle: 'solid', borderColor: meta.border, color: meta.ink }}
        title={meta.title || meta.label}
        data-testid={`sticker-${meta.id}`}
      >{meta.emoji}{meta.emoji && ' '}{meta.label}</span>
    );
  };

  const submitNew = async () => {
    const label = draft.label.trim();
    if (!label || !onCreateSticker) return;
    const palette = PRESET_PALETTES[draft.palette];
    const created = await onCreateSticker({ emoji: draft.emoji.trim(), label, ...palette });
    if (created?.id) onToggle?.(created.id);
    setDraft({ emoji: '', label: '', palette: 0 });
    setCreating(false);
  };

  return (
    <span className="inline-flex items-center gap-1 flex-nowrap whitespace-nowrap">
      {shown.map((id) => renderPill(id))}
      {canEdit && (
        <button
          ref={btnRef}
          type="button"
          onClick={openPicker}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-pink-600 hover:border-pink-300"
          title="add / remove a sticker"
          data-testid="sticker-add-btn"
        ><Plus className="w-2.5 h-2.5" /></button>
      )}
      {open && anchor && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => { setOpen(false); setCreating(false); }} />
          <div
            className="fixed z-[9999] w-72 bg-white rounded-lg shadow-xl border border-slate-200 p-2"
            style={{ top: anchor.top, left: anchor.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 px-1 mb-1">stickers</div>
            {STICKERS.map((s) => {
              const isAuto = (s.id === 'pp' && hasPP) || (s.id === 'engaged' && hasEngaged);
              const manuallyOn = manualStickers.includes(s.id);
              const on = isAuto || manuallyOn;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onToggle?.(s.id); setOpen(false); }}
                  className={`w-full flex items-center gap-2 text-left px-1.5 py-1.5 rounded text-xs ${on ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                  data-testid={`sticker-pick-${s.id}`}
                  title={s.title}
                >
                  <span className="text-base leading-none">{s.emoji}</span>
                  <span className="font-medium" style={{ color: s.ink }}>{s.label}</span>
                  {isAuto && !manuallyOn && <span className="ml-auto text-[9px] uppercase tracking-widest text-slate-400">auto</span>}
                  {manuallyOn && <span className="ml-auto text-[9px] uppercase tracking-widest text-amber-700">on</span>}
                </button>
              );
            })}

            {customStickers.length > 0 && (
              <>
                <div className="border-t border-slate-100 my-1" />
                <div className="text-[9px] uppercase tracking-widest text-slate-400 px-1 py-1">custom</div>
                {customStickers.map((s) => {
                  const on = manualStickers.includes(s.id);
                  return (
                    <div key={s.id} className={`group flex items-center gap-2 px-1.5 py-1.5 rounded text-xs ${on ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                      <button
                        type="button"
                        onClick={() => { onToggle?.(s.id); setOpen(false); }}
                        className="flex-1 flex items-center gap-2 text-left"
                        data-testid={`sticker-pick-${s.id}`}
                      >
                        {s.emoji && <span className="text-base leading-none">{s.emoji}</span>}
                        <span className="font-medium" style={{ color: s.ink }}>{s.label}</span>
                        {on && <span className="ml-auto text-[9px] uppercase tracking-widest text-amber-700">on</span>}
                      </button>
                      {onDeleteSticker && (
                        <button
                          type="button"
                          onClick={() => { if (window.confirm(`Delete "${s.label}" from every pitch?`)) onDeleteSticker(s.id); }}
                          className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600 shrink-0"
                          title="delete sticker"
                        ><Trash2 className="w-3 h-3" /></button>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            <div className="border-t border-slate-100 mt-1 pt-1">
              {!creating ? (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="w-full flex items-center gap-1.5 px-1.5 py-1.5 rounded text-xs text-pink-600 hover:bg-pink-50 font-medium"
                  data-testid="sticker-new-btn"
                >
                  <Plus className="w-3 h-3" /> new sticker
                </button>
              ) : (
                <div className="p-1.5 space-y-1.5">
                  <div className="flex gap-1.5">
                    <input
                      value={draft.emoji}
                      onChange={(e) => setDraft({ ...draft, emoji: e.target.value })}
                      placeholder="🎯"
                      className="w-10 h-7 text-center text-sm border border-slate-200 rounded outline-none focus:ring-2 focus:ring-pink-200"
                      data-testid="sticker-new-emoji"
                    />
                    <input
                      autoFocus
                      value={draft.label}
                      onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); if (e.key === 'Escape') setCreating(false); }}
                      placeholder="label"
                      className="flex-1 h-7 px-2 text-xs border border-slate-200 rounded outline-none focus:ring-2 focus:ring-pink-200"
                      data-testid="sticker-new-label"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    {PRESET_PALETTES.map((p, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setDraft({ ...draft, palette: idx })}
                        className={`w-5 h-5 rounded-full transition ${draft.palette === idx ? 'ring-2 ring-pink-400 ring-offset-1' : ''}`}
                        style={{ background: p.bg, borderWidth: 1, borderStyle: 'solid', borderColor: p.border }}
                        title={`colour ${idx + 1}`}
                      />
                    ))}
                    <div className="ml-auto flex gap-1">
                      <button type="button" onClick={() => setCreating(false)} className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800">cancel</button>
                      <button type="button" onClick={submitNew} disabled={!draft.label.trim()} className="h-7 px-3 text-[11px] font-semibold rounded bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-40" data-testid="sticker-new-save">save</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-1 border-t border-slate-100 pt-1 text-[10px] text-slate-400 px-1">tapping toggles the manual override</div>
          </div>
        </>,
        document.body,
      )}
    </span>
  );
};

export default StickersRail;
